/**
 * Hooks — deterministic interception points around tool execution, run by the
 * harness/host (mirrors Claude Code hooks). The seam stays edge-safe: hooks are
 * plain async callbacks, no node builtins, injected via AgentOptions.hooks.
 *
 *  - preToolUse:  fires BEFORE a tool runs. Return `{ block: true, reason }` to
 *                 skip the tool — Agent.dispatch returns `Blocked by hook: <reason>`
 *                 as the tool result (the model sees it and can adapt).
 *  - postToolUse: fires AFTER a tool produced its result string (observe/audit).
 *  - onStop:      fires once when the loop terminates with finishReason 'stop'.
 */
import type { Message } from './llm';

export interface ToolUse {
  name: string;
  args: any;
}

export interface PreToolUseDecision {
  block?: boolean;
  reason?: string;
}

/** Per-call metadata threaded through dispatch — `id` is the model-supplied tool_call id,
 *  letting a host correlate a result to its call without assuming serial dispatch order. */
export interface ToolUseMeta {
  id?: string;
}

export interface Hooks {
  /** Guard a tool call before it runs; return `{ block, reason }` to skip it. */
  preToolUse?(call: ToolUse, meta?: ToolUseMeta): Promise<PreToolUseDecision | void> | PreToolUseDecision | void;
  /** Observe a tool's result after it ran (audit, metrics, side-channel). */
  postToolUse?(call: ToolUse, result: string, meta?: ToolUseMeta): void | Promise<void>;
  /** Observe a tool's INCREMENTAL output while it runs (only tools that stream, e.g. the real
   *  Shell). Fire-and-forget — sync, never awaited, never alters the result. */
  onToolOutput?(call: ToolUse, chunk: string, meta?: ToolUseMeta): void;
  /** Fired once when the agent loop stops cleanly with the model's final text. */
  onStop?(finalText: string): void;
  // --- lifecycle (all optional; fired from well-known points in run/send/compact) ---
  /** Fired once at session start (a fresh `run()`, or the first `send()`). Return a string to inject
   *  as extra system context (e.g. environment facts, project status). Multiple hooks concatenate. */
  onSessionStart?(): string | void | Promise<string | void>;
  /** Fired per user turn BEFORE the model call. Return a string to replace the submitted text
   *  (rewrite/annotate the prompt); chained across hooks. Return void to leave it unchanged. */
  onUserPromptSubmit?(text: string): string | void | Promise<string | void>;
  /** Fired before a manual `compactNow()` folds older messages — observe (e.g. persist a summary). */
  onPreCompact?(messages: Message[]): void | Promise<void>;
  /** Fired when a `Task`/`TaskBatch` child agent finishes, with its summary + label. */
  onSubagentStop?(summary: string, info?: { label?: string; agentType?: string }): void | Promise<void>;
}

/** Deterministic Hooks for tests/dev: records calls + scripts block decisions by tool name. */
export class RecordingHooks implements Hooks {
  public pre: Array<{ call: ToolUse; meta?: ToolUseMeta }> = [];
  public post: Array<{ call: ToolUse; result: string; meta?: ToolUseMeta }> = [];
  public outputs: Array<{ call: ToolUse; chunk: string; meta?: ToolUseMeta }> = [];
  public stops: string[] = [];
  /** tool name -> reason; a matching preToolUse call is blocked with that reason. */
  constructor(private blocks: Record<string, string> = {}) {}
  preToolUse(call: ToolUse, meta?: ToolUseMeta): PreToolUseDecision | void {
    this.pre.push({ call, meta });
    const reason = this.blocks[call.name];
    if (reason != null) return { block: true, reason };
  }
  postToolUse(call: ToolUse, result: string, meta?: ToolUseMeta): void {
    this.post.push({ call, result, meta });
  }
  onToolOutput(call: ToolUse, chunk: string, meta?: ToolUseMeta): void {
    this.outputs.push({ call, chunk, meta });
  }
  onStop(finalText: string): void {
    this.stops.push(finalText);
  }
}

/** Recording lifecycle hooks for tests: capture session-start/prompt-submit/pre-compact + script transforms. */
export class RecordingLifecycle implements Hooks {
  public starts = 0;
  public prompts: string[] = [];
  public compactions: number[] = [];
  /** @param startContext injected at session start; @param rewrite maps a submitted prompt to a new one. */
  constructor(private startContext?: string, private rewrite?: (t: string) => string) {}
  public subagentStops: Array<{ summary: string; label?: string }> = [];
  onSessionStart(): string | void { this.starts++; return this.startContext; }
  onUserPromptSubmit(text: string): string | void { this.prompts.push(text); return this.rewrite?.(text); }
  onPreCompact(messages: Message[]): void { this.compactions.push(messages.length); }
  onSubagentStop(summary: string, info?: { label?: string }): void { this.subagentStops.push({ summary, label: info?.label }); }
}
